import { useState } from "react";
import { motion } from "framer-motion";
import { ExternalLink, Music4, Play, X } from "lucide-react";

const SPOTIFY_ARTIST_ID = "57G8pX7tRFjvmsFHPkt2iP";
const SPOTIFY_URL = `https://open.spotify.com/intl-fr/artist/${SPOTIFY_ARTIST_ID}`;
const SOUNDCLOUD_URL = "https://soundcloud.com/user-408608435";

const TRACKS = [
  {
    id: "7BwEQlhLN604Jg7MdYXQbm",
    title: "ALLEZ LES BLEUS",
    subtitle: "Po Po Po Pooo Po · Radio Edit",
    cover:
      "https://i.scdn.co/image/ab67616d00001e02a49de331ae21c86cbb71b0a7",
  },
  {
    id: "0CfFTeLKHHcVc2zYV9p3gy",
    title: "RAVAGE",
    subtitle: "Radio Edit",
    cover:
      "https://i.scdn.co/image/ab67616d00001e02956f0f0f2aaa949e86ccc6b3",
  },
  {
    id: "5JrSvrBdtlanSGRmfNqkVR",
    title: "Hysteria",
    subtitle: "DJ Fab Remix · Radio Edit",
    cover:
      "https://i.scdn.co/image/ab67616d00001e0243ae77894876113a3e1e8071",
  },
  {
    id: "7FaTacQkhkTVRd0ryl7OXy",
    title: "Noche Verano",
    subtitle: "DJ Nexha · Radio Edit",
    cover:
      "https://i.scdn.co/image/ab67616d00001e02586d7fb225f0163b68bac9cf",
  },
  {
    id: "77FHlWLvdhvwNf7JetvCH1",
    title: "Ravage #2",
    subtitle: "DJ Nexha Remix · Radio Edit",
    cover:
      "https://i.scdn.co/image/ab67616d00001e029b6dbcf35e1eb5d28cf7727f",
  },
];

const SpotifyIcon = ({ size = 20 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
    <path d="M12 0a12 12 0 1 0 0 24 12 12 0 0 0 0-24zm5.5 17.31a.75.75 0 0 1-1.03.25c-2.82-1.72-6.37-2.11-10.55-1.16a.75.75 0 1 1-.34-1.46c4.57-1.04 8.5-.6 11.66 1.34.36.22.47.69.26 1.03zm1.47-3.27a.94.94 0 0 1-1.29.31c-3.23-1.99-8.16-2.56-11.99-1.4a.94.94 0 1 1-.55-1.8c4.38-1.33 9.81-.69 13.51 1.6a.94.94 0 0 1 .32 1.29zm.13-3.4c-3.87-2.3-10.27-2.51-13.97-1.39a1.13 1.13 0 1 1-.66-2.16c4.25-1.29 11.32-1.04 15.78 1.6a1.13 1.13 0 1 1-1.15 1.95z" />
  </svg>
);

export const SpotifySection = () => {
  const [playing, setPlaying] = useState(null);
  return (
    <section
      id="music"
      data-testid="spotify-section"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[var(--bg-surface)] border-t border-white/5 overflow-hidden"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(29,185,84,0.14),transparent_60%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(212,175,55,0.10),transparent_60%)]" />

      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-12">
          <div className="max-w-2xl">
            <span className="eyebrow inline-flex items-center gap-2">
              <Music4 size={13} /> Mes créations musicales
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              Pas seulement DJ. <span className="gold-text-gradient">Collaborateur aussi.</span>
            </h2>
            <p className="mt-5 text-zinc-300 leading-relaxed">
              Au-delà du DJing, j'ai eu la chance de collaborer avec plusieurs artistes
              sur des morceaux disponibles en streaming sur Spotify. Une dimension
              supplémentaire qui me permet d'apporter un univers musical unique à
              chaque événement.
            </p>
          </div>

          <a
            href={SPOTIFY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="spotify-open-btn"
            className="self-start lg:self-auto inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold bg-[#1DB954] text-black hover:bg-[#1ed760] transition-all duration-300 hover:-translate-y-0.5 shadow-[0_10px_30px_rgba(29,185,84,0.35)]"
          >
            <SpotifyIcon size={18} /> Ouvrir sur Spotify
          </a>
        </div>

        <div className="-mt-4 mb-10 flex flex-wrap gap-2 justify-end">
          <a
            href={SOUNDCLOUD_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="soundcloud-open-btn"
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-semibold border border-[#FF7700]/40 text-[#FF8A1F] hover:bg-[#FF7700]/10 transition"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M21 16a3 3 0 0 0-1.07-2.31c-.16-.13-.34-.25-.53-.34-.05-3.5-2.93-6.35-6.4-6.35-1.65 0-3.16.63-4.31 1.67-.16.14-.18.21-.18.42v9.7c0 .19.14.34.32.36H21a2 2 0 0 0 2-2 3 3 0 0 0-2-3.15zM7 19h1v-9.7c0-.21-.17-.39-.38-.39-.21 0-.38.18-.38.39V19zm-2 0h1v-8c0-.21-.17-.38-.38-.38S5.24 10.79 5.24 11v8H5zm-2 0h1v-7c0-.21-.17-.38-.38-.38S3.24 11.79 3.24 12v7H3zm-2 0h1v-6c0-.21-.17-.38-.38-.38S1.24 12.79 1.24 13v6H1z"/>
            </svg>
            Aussi sur SoundCloud
          </a>
        </div>

        <div
          data-testid="spotify-tracks-grid"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 md:gap-5"
        >
          {TRACKS.map((t, i) => {
            const isPlaying = playing === t.id;
            return (
              <motion.div
                key={t.id}
                data-testid={`spotify-track-${t.id}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.55, delay: i * 0.07 }}
                className={`group relative rounded-2xl overflow-hidden border bg-[#0A0A0A] transition-all hover:-translate-y-1 ${
                  isPlaying ? "border-[#1DB954]/70 shadow-[0_0_40px_rgba(29,185,84,0.25)]" : "border-white/10 hover:border-[#1DB954]/50"
                }`}
              >
                <div className="relative aspect-square overflow-hidden bg-black">
                  {isPlaying ? (
                    <>
                      <iframe
                        src={`https://open.spotify.com/embed/track/${t.id}?utm_source=generator&theme=0`}
                        title={`Lecteur Spotify – ${t.title}`}
                        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                        loading="lazy"
                        className="absolute inset-0 w-full h-full border-0"
                        data-testid={`spotify-player-${t.id}`}
                      />
                      <button
                        type="button"
                        onClick={() => setPlaying(null)}
                        aria-label="Fermer le lecteur"
                        data-testid={`spotify-close-${t.id}`}
                        className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/80 text-white flex items-center justify-center hover:bg-[#1DB954] hover:text-black transition-colors"
                      >
                        <X size={14} />
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setPlaying(t.id)}
                      aria-label={`Écouter ${t.title}`}
                      data-testid={`spotify-play-${t.id}`}
                      className="absolute inset-0 w-full h-full text-left"
                    >
                      <img
                        src={t.cover}
                        alt={`${t.title} – Adrien Sebert DJ AS`}
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-transparent" />
                      <div className="absolute bottom-3 right-3 w-12 h-12 rounded-full bg-[#1DB954] text-black flex items-center justify-center shadow-[0_10px_30px_rgba(29,185,84,0.5)] opacity-90 group-hover:opacity-100 group-hover:scale-110 transition-all duration-300">
                        <Play size={18} className="translate-x-0.5 fill-current" />
                      </div>
                      <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/70 backdrop-blur text-[#1DB954] text-[10px] tracking-[0.18em] uppercase font-semibold">
                        <SpotifyIcon size={11} /> Spotify
                      </div>
                    </button>
                  )}
                </div>

                <div className="p-4 md:p-5">
                  <div className="text-white font-semibold tracking-tight text-base md:text-lg leading-tight line-clamp-1">
                    {t.title}
                  </div>
                  <div className="mt-1.5 text-xs text-zinc-400 leading-snug line-clamp-2">
                    {t.subtitle}
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => setPlaying(isPlaying ? null : t.id)}
                      data-testid={`spotify-toggle-${t.id}`}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1DB954] hover:text-[#1ed760]"
                    >
                      {isPlaying ? <><X size={12} /> Fermer</> : <><Play size={12} className="fill-current" /> Écouter ici</>}
                    </button>
                    <a
                      href={`https://open.spotify.com/track/${t.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`spotify-open-track-${t.id}`}
                      className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-white transition-colors"
                    >
                      Spotify <ExternalLink size={11} />
                    </a>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <p className="text-center text-zinc-500 text-sm mt-12">
          Retrouvez l'intégralité de mes productions, remixes et collaborations sur{" "}
          <a
            href={SPOTIFY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#1DB954] hover:underline font-medium"
          >
            mon profil Spotify
          </a>
          .
        </p>
      </div>
    </section>
  );
};
